import React, { useState, useEffect } from 'react';
import { ProrationRule, ProrationTarget } from '../types';
import { AddIcon, DeleteIcon } from './icons';

interface ProrationRuleModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (ruleData: Omit<ProrationRule, 'id'>, id?: string) => void;
    categories: string[];
    ruleToEdit?: ProrationRule | null;
}

const ProrationRuleModal: React.FC<ProrationRuleModalProps> = ({ isOpen, onClose, onSave, categories, ruleToEdit }) => {
    const [name, setName] = useState('');
    const [sourceCategory, setSourceCategory] = useState('');
    const [sourcePercentage, setSourcePercentage] = useState<string | number>(100);
    const [targets, setTargets] = useState<ProrationTarget[]>([]);

    useEffect(() => {
        if (isOpen) {
            if (ruleToEdit) {
                setName(ruleToEdit.name);
                setSourceCategory(ruleToEdit.sourceCategory);

                const sourceTarget = ruleToEdit.targets.find(t => t.category === ruleToEdit.sourceCategory);
                setSourcePercentage(sourceTarget ? sourceTarget.percentage : 0);

                const otherTargets = ruleToEdit.targets.filter(t => t.category !== ruleToEdit.sourceCategory);
                setTargets(otherTargets);
            } else {
                // Reset for new rule
                setName('');
                setSourceCategory('');
                setSourcePercentage(100);
                setTargets([]);
            }
        }
    }, [ruleToEdit, isOpen]);

    const handleTargetChange = (index: number, field: keyof ProrationTarget, value: string | number) => {
        const newTargets = [...targets];
        if (field === 'percentage') {
            newTargets[index][field] = Number(value);
        } else {
            newTargets[index][field] = value as string;
        }
        setTargets(newTargets);
    };

    const addTarget = () => {
        setTargets([...targets, { category: '', percentage: 0 }]);
    };

    const removeTarget = (index: number) => {
        setTargets(targets.filter((_, i) => i !== index));
    };

    const totalPercentage = (Number(sourcePercentage) || 0) + targets.reduce((sum, target) => sum + (target.percentage || 0), 0);
    const isTotalPercentageCorrect = Math.abs(totalPercentage - 100) < 0.001;
    const areTargetsValid = targets.every(t => t.category);

    const isValid = isTotalPercentageCorrect && name.trim() && sourceCategory && areTargetsValid;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!isValid) {
            alert('Asegúrate de que el nombre y la categoría de origen estén completos, que cada destino tenga una categoría asignada, y que el total de los porcentajes sea exactamente 100%.');
            return;
        }

        const finalTargets: ProrationTarget[] = [];
        const numericSourcePercentage = Number(sourcePercentage) || 0;

        if (numericSourcePercentage > 0) {
            finalTargets.push({ category: sourceCategory, percentage: numericSourcePercentage });
        }

        const validSharedTargets = targets.filter(t => t.percentage > 0);
        finalTargets.push(...validSharedTargets);

        onSave({ name, sourceCategory, targets: finalTargets }, ruleToEdit?.id);
    };

    if (!isOpen) return null;

    const availableCategories = categories.filter(c => c !== "Desconocida");

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex justify-center items-center" onClick={onClose}>
            <div className="bg-light-surface dark:bg-surface rounded-lg shadow-xl p-8 w-[95%] sm:w-full max-w-2xl border border-light-border dark:border-border" onClick={e => e.stopPropagation()}>
                <h2 className="text-2xl font-bold text-light-text-primary dark:text-text-primary mb-6">{ruleToEdit ? 'Editar Regla de Prorrateo' : 'Nueva Regla de Prorrateo'}</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label htmlFor="ruleName" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Nombre de la Regla</label>
                        <input type="text" id="ruleName" value={name} onChange={e => setName(e.target.value)} required className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2" placeholder="Ej: Gastos de Oficina"/>
                    </div>
                    
                    <div>
                        <label htmlFor="sourceCategory" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Categoría de Gasto Origen</label>
                        <div className="flex items-center gap-2">
                            <select id="sourceCategory" value={sourceCategory} onChange={e => setSourceCategory(e.target.value)} required className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2">
                                <option value="" disabled>Selecciona una categoría</option>
                                {availableCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                            </select>
                            <input 
                                type="number" 
                                value={sourcePercentage} 
                                onChange={e => setSourcePercentage(e.target.value)} 
                                min="0" 
                                max="100" 
                                step="0.01" 
                                className="w-32 bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 text-center" 
                                placeholder="%" 
                                required
                                aria-label="Porcentaje de la categoría de origen"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-2">Gastos compartidos con</label>
                        <div className="space-y-3 p-3 bg-gray-100 dark:bg-gray-800/50 rounded-lg border border-light-border dark:border-border">
                            {targets.map((target, index) => (
                                <div key={index} className="flex items-center gap-2">
                                    <select value={target.category} onChange={e => handleTargetChange(index, 'category', e.target.value)} className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2" required>
                                        <option value="" disabled>Categoría destino</option>
                                        {availableCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                    </select>
                                    <input type="number" value={target.percentage} onChange={e => handleTargetChange(index, 'percentage', e.target.value)} min="0" max="100" step="0.01" className="w-32 bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 text-center" placeholder="%" required/>
                                    <button type="button" onClick={() => removeTarget(index)} className="p-2 text-light-text-secondary dark:text-text-secondary hover:text-red-500">
                                        <DeleteIcon className="w-5 h-5"/>
                                    </button>
                                </div>
                            ))}
                            <button type="button" onClick={addTarget} className="flex items-center gap-2 text-sm text-secondary font-medium hover:text-green-400 p-2">
                                <AddIcon className="w-5 h-5"/> Añadir Destino
                            </button>
                        </div>
                        <div className={`mt-2 text-right font-medium text-sm ${isTotalPercentageCorrect ? 'text-green-500' : 'text-red-500'}`}>
                            Total: {totalPercentage.toFixed(2)}%
                        </div>
                    </div>
                    
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500">Cancelar</button>
                        <button type="submit" disabled={!isValid} className="px-6 py-2 rounded-md bg-primary hover:bg-blue-600 text-white font-semibold disabled:bg-gray-500 dark:disabled:bg-gray-700 disabled:cursor-not-allowed">
                            {ruleToEdit ? 'Guardar Cambios' : 'Crear Regla'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProrationRuleModal;
